import { ReactElement } from 'react';
import { useForm, Resolver } from 'react-hook-form';
import { Trans, t } from '@lingui/macro';

import { LoginContainer, StyledLoginForm, StyledShadowedContainer } from './login.styles';
import { Password, Input } from 'components/forms';
import { PrimaryButtonM } from 'components/button';
import { LoginFormErrorProps, LoginFormProps } from './login.types';
import { useLoginMutation } from "models/auth/auth.queries";
import background from 'assets/login_background.png';
import { FeedbackNotificationError } from 'components/feedback-notification';

const useCustomResolver: Resolver<LoginFormProps> = (data: LoginFormProps) => {
  const REQUIRED_FIELD_MESSAGE = t`This field is required`;
  const errors: LoginFormErrorProps = {};

  if (!data.username) {
    errors.username = { message: REQUIRED_FIELD_MESSAGE };
  }

  if (!data.password) {
    errors.password = { message: REQUIRED_FIELD_MESSAGE };
  }

  return { values: data, errors };
};

export default function Login(): ReactElement {
  const { mutate, isPending, isError, error } = useLoginMutation();
  
  const submitLoginForm = (data: LoginFormProps, e?: React.BaseSyntheticEvent): void => {
    e?.preventDefault();
    // Handle submit form
    mutate({
      username: data.username,
      password: data.password
    })
  };

  const { handleSubmit, control } = useForm({ mode: 'onBlur', resolver: useCustomResolver });
  const loginErrorMessage = isError ? error?.response?.data?.code : '';
  return (
    <LoginContainer style={{ backgroundImage: `url(${background || ''}` }}>
      <StyledShadowedContainer className='login-box'>
        <StyledLoginForm onSubmit={handleSubmit(submitLoginForm)} className='login-form'>
          {isError &&
            <FeedbackNotificationError
              message={loginErrorMessage}
            />
          }
          {/* INPUTS */}
          <Input 
            name='username'
            label={t`User name`}
            boxClassName='mt-6'
            control={control}
            disabled={isPending}
          />
          <Password 
            name='password'
            label={t`Password`}
            boxClassName='mt-6'
            control={control}
            disabled={isPending}
          />

          {/* BUTTONS */}
          <PrimaryButtonM
            className='login-button mt-6'
            disabled={isPending}
            aria-label={t`Login`}
            testId='form-button-login-submit'
          >
            <Trans>Login</Trans>
          </PrimaryButtonM>
        </StyledLoginForm>
      </StyledShadowedContainer>
    </LoginContainer>
  );
}
